import { useTranslation } from 'react-i18next';
import {
  SalesPerformanceAggregation,
  SalesPerformanceRow,
  StoreSalesPerformanceResponse,
} from '../../../../hooks';
import { storeOverviewTranslations } from '../../../../translations';

type SalesPerformanceRowKey = keyof SalesPerformanceRow;
type SalesPerformanceAggregationKey = keyof SalesPerformanceAggregation;
export type StoreSalesPerformanceResponseKey = keyof StoreSalesPerformanceResponse;
export type SalesPerformancePeriod =
  | 'currentDay'
  | 'currentWeek'
  | 'lastCompleteWeek'
  | 'lastFourWeeks'
  | 'accumulatedFinancialYearToDate';

export const salesPerformancePeriods: SalesPerformancePeriod[] = [
  'currentDay',
  'currentWeek',
  'lastCompleteWeek',
  'lastFourWeeks',
  'accumulatedFinancialYearToDate',
];

export type ChipKey = 'LCD' | 'CW' | 'LCW' | 'L4Wks' | 'AccTY';

export type ChipOption = {
  key: ChipKey;
  label: string;
  tooltip?: string;
};

export const useChipOptionsMap = () => {
  const { t } = useTranslation();
  const { salesPerformance: salesPerformanceTranslations, chipTooltips: chipTooltipsTranslations } =
    storeOverviewTranslations;

  const chipOptionsMap: Record<ChipKey, ChipOption> = {
    LCD: {
      key: 'LCD',
      label: t(
        salesPerformanceTranslations.selectedDay.key,
        salesPerformanceTranslations.selectedDay.defaultValue,
      ),
      tooltip: t(
        chipTooltipsTranslations.lastCompleteDay.key,
        chipTooltipsTranslations.lastCompleteDay.defaultValue,
      ),
    },
    CW: {
      key: 'CW',
      label: t(
        salesPerformanceTranslations.currentWeek.key,
        salesPerformanceTranslations.currentWeek.defaultValue,
      ),
      tooltip: t(chipTooltipsTranslations.currentWeek.key, chipTooltipsTranslations.currentWeek.defaultValue),
    },
    LCW: {
      key: 'LCW',
      label: 'LCW',
      tooltip: t(
        chipTooltipsTranslations.lastCompleteWeek.key,
        chipTooltipsTranslations.lastCompleteWeek.defaultValue,
      ),
    },
    L4Wks: {
      key: 'L4Wks',
      label: 'L4Wks',
      tooltip: t(chipTooltipsTranslations.lastFourWeeks.key, chipTooltipsTranslations.lastFourWeeks.defaultValue),
    },
    AccTY: {
      key: 'AccTY',
      label: 'AccTY',
      tooltip: t(
        chipTooltipsTranslations.accumulatedFinancialYearToDate.key,
        chipTooltipsTranslations.accumulatedFinancialYearToDate.defaultValue,
      ),
    },
  };

  return { chipOptionsMap };
};

export const chipDataMapping: Record<ChipKey, SalesPerformancePeriod> = {
  LCD: 'currentDay',
  CW: 'currentWeek',
  LCW: 'lastCompleteWeek',
  L4Wks: 'lastFourWeeks',
  AccTY: 'accumulatedFinancialYearToDate',
};

const defaultRow: SalesPerformanceRow = {
  salesNet: 0,
  salesNetLastLastWeek: 0,
  salesNetLastYear: 0,
  salesNetArea: 0,
  salesNetAreaLastYear: 0,
  salesNetMarket: 0,
  salesNetMarketLastYear: 0,
  nonFullPriceSales: 0,
  nonFullPriceSalesArea: 0,
  nonFullPriceSalesLastYear: 0,
  nonFullPriceSalesLastYearArea: 0,
  soldPieces: 0,
  soldPiecesLastYear: 0,
  soldPiecesArea: 0,
  soldPiecesAreaLastYear: 0,
  soldPiecesMarket: 0,
  soldPiecesMarketLastYear: 0,
};

export const getIndex = (currentValue: number, previousValue: number, shouldRoundOff: boolean = true) => {
  if (previousValue === 0) return 0;
  const indexValue = (currentValue / previousValue) * 100;
  return shouldRoundOff ? Math.round(indexValue) : indexValue;
};

// Get the filter name based on the selected index filter
export const getIndexFilterName = (
  salesPerformanceData: StoreSalesPerformanceResponse | null,
  selectedIndexFilter: string | undefined,
) => {
  if (!salesPerformanceData || !selectedIndexFilter) return '';
  const allAggregations = [
    ...salesPerformanceData.currentDay.departmentDetails,
    ...salesPerformanceData.lastCompleteDay.departmentDetails,
    ...salesPerformanceData.currentWeek.departmentDetails,
    ...salesPerformanceData.lastCompleteWeek.departmentDetails,
    ...salesPerformanceData.lastFourWeeks.departmentDetails,
    ...salesPerformanceData.accumulatedFinancialYearToDate.departmentDetails,
  ];

  const filterObject = allAggregations.find((item) => item.index === selectedIndexFilter);

  return filterObject ? `${selectedIndexFilter}-${filterObject.indexDescription}` : '';
};

// Sum up sales performance rows, optionally filtering by index
export const sumRows = (rows: SalesPerformanceAggregation[], filterIndex?: string): SalesPerformanceRow => {
  const filteredRows = filterIndex ? rows.filter((row) => row.index === filterIndex) : rows;

  if (filteredRows.length === 0) {
    return defaultRow;
  }

  const result = filteredRows.reduce((acc, { sales }) => {
    Object.keys(sales).forEach((key) => {
      const value = key as SalesPerformanceRowKey;
      acc[value] = (acc[value] || 0) + sales[value];
    });
    return acc;
  }, {} as SalesPerformanceRow);

  return { ...defaultRow, ...result };
};

export const getAggregatedDataFilterByIndex = (response: StoreSalesPerformanceResponse, filterIndex?: string) => {
  const aggregatedData = {
    currentDay: sumRows(response.currentDay.departmentDetails, filterIndex),
    lastCompleteDay: sumRows(response.lastCompleteDay.departmentDetails, filterIndex),
    currentWeek: sumRows(response.currentWeek.departmentDetails, filterIndex),
    lastCompleteWeek: sumRows(response.lastCompleteWeek.departmentDetails, filterIndex),
    lastFourWeeks: sumRows(response.lastFourWeeks.departmentDetails, filterIndex),
    accumulatedFinancialYearToDate: sumRows(
      response.accumulatedFinancialYearToDate.departmentDetails,
      filterIndex,
    ),
  };

  return aggregatedData;
};

export const getIndexStatus = (value: number) => (value >= 100 ? 'positive' : 'negative');
export const getRankStatus = (value: number) => (value > 0 ? 'positive' : 'negative');

export const getDifferenceText = (value: number, showPercentage: boolean) => {
  const roundedValue = Math.round(value);
  const formattedValue = roundedValue < 0 ? roundedValue.toString() : `+${roundedValue}`;

  return showPercentage ? `${formattedValue}%` : formattedValue;
};

// --- SALES BY INDEX/SUBINDEX AND SALES BY GARMENTGROUP/DEPARTMENT AGGREGATOR AND HELPERS --- //

export type SalesComputedData = {
  salesNetThisYear: number;
  differenceLastYear: number;
  sob: number;
  drivingUnit: number;
  indexLastYear: number;
  lastYearVsArea: number;
  lastYearVsSalesMarket: number;
};

export type SalesAggregatedRow = SalesComputedData & {
  metric: string;
};

export type SalesAggregatedData = {
  [key in StoreSalesPerformanceResponseKey]: {
    [key: string]: SalesAggregatedRow;
    total: SalesAggregatedRow;
  };
};

const calculateSalesByIndexTableData = (
  item: SalesPerformanceRow,
  totalData: SalesPerformanceRow,
): SalesComputedData => {
  const {
    salesNet,
    salesNetLastYear,
    salesNetArea,
    salesNetAreaLastYear,
    salesNetMarket,
    salesNetMarketLastYear,
  } = item;

  const { salesNet: totalSalesNetTY, salesNetLastYear: totalSalesNetLastYear } = totalData;

  const areaIndex = getIndex(salesNetArea, salesNetAreaLastYear);
  const smIndex = getIndex(salesNetMarket, salesNetMarketLastYear);

  const differenceLastYear = salesNet - salesNetLastYear;
  const sob = totalSalesNetTY === 0 ? 0 : (salesNet / totalSalesNetTY) * 100;
  const drivingUnit = totalSalesNetLastYear === 0 ? 0 : (differenceLastYear / totalSalesNetLastYear) * 100;

  const indexLastYear = getIndex(salesNet, salesNetLastYear);
  const lastYearVsArea = getIndex(indexLastYear, areaIndex);
  const lastYearVsSalesMarket = getIndex(indexLastYear, smIndex);

  return {
    salesNetThisYear: salesNet,
    differenceLastYear: differenceLastYear,
    sob,
    drivingUnit,
    indexLastYear,
    lastYearVsArea,
    lastYearVsSalesMarket,
  };
};

const createSalesAggregator = (config: {
  primaryKey: SalesPerformanceAggregationKey;
  secondaryKey: SalesPerformanceAggregationKey;
  primaryLabelKey: SalesPerformanceAggregationKey;
  secondaryLabelKey: SalesPerformanceAggregationKey;
}) => {
  const { primaryKey, secondaryKey, primaryLabelKey, secondaryLabelKey } = config;

  return (data: StoreSalesPerformanceResponse, filterIndex?: string): SalesAggregatedData => {
    const result = {} as SalesAggregatedData;

    salesPerformancePeriods.forEach((period) => {
      const periodData = data[period].departmentDetails;
      const groupedData: Record<string, SalesPerformanceAggregation[]> = {};

      const filtered = filterIndex ? periodData.filter((r) => r.index === filterIndex) : periodData;

      filtered.forEach((item) => {
        const key = (filterIndex ? item[secondaryKey] : item[primaryKey]) as string;

        if (!groupedData[key]) {
          groupedData[key] = [];
        }

        groupedData[key].push(item);
      });

      const aggregatedRows: Record<string, SalesAggregatedRow> = {};

      const totalData = sumRows(periodData, filterIndex);

      Object.entries(groupedData).forEach(([groupKey, rows]) => {
        const summed = sumRows(rows);
        const computedData = calculateSalesByIndexTableData(summed, totalData);

        // remove rows with 0 salesNetThisYear and 0 differenceLastYear
        if (computedData.salesNetThisYear !== 0 && computedData.differenceLastYear !== 0) {
          const primaryMetricLabel = `${rows[0][primaryLabelKey]}`;
          let secondaryKeyMetricLabel = `${rows[0][secondaryLabelKey]}`;

          // If secondaryKey is departmentNumber, add departmentNumber
          if (secondaryKey === 'departmentNumber') {
            secondaryKeyMetricLabel = `${rows[0][secondaryKey]} ${rows[0].departmentName}`;
          }

          const metricLabel = filterIndex ? secondaryKeyMetricLabel : primaryMetricLabel;

          aggregatedRows[groupKey] = {
            metric: metricLabel,
            ...computedData,
          };
        }
      });

      const total = {
        metric: 'Total',
        ...calculateSalesByIndexTableData(sumRows(periodData, filterIndex), totalData),
      };

      result[period] = {
        ...aggregatedRows,
        total,
      };
    });

    return result;
  };
};

// create index/subIndex aggregator
export const getSalesByIndexData = createSalesAggregator({
  primaryKey: 'index',
  secondaryKey: 'subIndex',
  primaryLabelKey: 'indexDescription',
  secondaryLabelKey: 'subIndexDescription',
});

// create garmentGroup/department aggregator
export const getSalesByGarmentGroupData = createSalesAggregator({
  primaryKey: 'garmentGroup',
  secondaryKey: 'departmentNumber',
  primaryLabelKey: 'garmentGroup',
  secondaryLabelKey: 'departmentName',
});

// --- TOP SOLD PRODUCTS AND TOP PLANNED PRODUCTS HELPERS --- //

export type SoldProductItemRankedAreaSalesMarket = {
  subIndexId: string;
  articleIdentifier: string;
  subIndexDescription: string;
  departmentName: string;
  departmentNumber: string;
  productId: string;
  productName: string;
  netSale: number;
  soldPieces: number;
  price: number;
  differenceLastLastWeek: number;
  rankingVsArea: number;
  rankingVsSalesMarket: number;
};

export type PlanProductItemRankedAreaSalesMarket = {
  subIndexId: string;
  subIndexDescription: string;
  departmentName: string;
  departmentNumber: string;
  productId: string;
  articleIdentifier: string;
  productName: string;
  netSale: number;
  planSales: number;
  soldPieces: number;
  price: number;
  differenceLastLastWeek: number;
  rankingVsArea: number;
  rankingVsSalesMarket: number;
};

// TYPEGUARD FOR SOLDPRODUCT VS PLANPRODUCT

export const isSoldProductItem = (
  item: SoldProductItemRankedAreaSalesMarket | PlanProductItemRankedAreaSalesMarket,
): item is SoldProductItemRankedAreaSalesMarket => {
  return (
    (item as SoldProductItemRankedAreaSalesMarket).netSale !== undefined &&
    (item as SoldProductItemRankedAreaSalesMarket).soldPieces !== undefined &&
    (item as PlanProductItemRankedAreaSalesMarket).planSales === undefined
  );
};
